'use client';

import { Dropdown, LoadingIndicator, TextField, TextPassage } from '@petsmart-ui/sparky';
import { useField } from 'formik';
import * as React from 'react';
import { useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';

import { useOnClickOutside } from '@libs/common-next';

import type { AddressFormInterface, SuggestedAddressInterface, SuggestionResponseInterface } from './types';
import { getFieldErrorMessage } from './utils';

const componentClassname = 'street-address-line';

export interface StreetAddressLineInterface {
    formLibrary?: 'formik' | 'rhf';
    dropdownIsOpen: boolean;
    setDropdownIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
    suggestedAddresses: SuggestedAddressInterface[] | null;
    handleSuggestedAddressClick: (address: SuggestedAddressInterface) => void | Promise<void>;
    loading: boolean;
    setLoading: React.Dispatch<React.SetStateAction<boolean>>;
    checkAddressSuggestions: (value: string) => Promise<SuggestionResponseInterface | null>;
    setSuggestedAddresses: React.Dispatch<React.SetStateAction<SuggestedAddressInterface[] | null>>;
}

export function StreetAddressLine({
    formLibrary = 'formik',
    dropdownIsOpen,
    suggestedAddresses,
    handleSuggestedAddressClick,
    loading,
    setLoading,
    checkAddressSuggestions,
    setDropdownIsOpen,
    setSuggestedAddresses,
}: StreetAddressLineInterface) {
    const timeoutIdRef = useRef<NodeJS.Timeout | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    const clearSuggestions = () => {
        setDropdownIsOpen(false);
    };

    useOnClickOutside(containerRef, clearSuggestions);

    const formatMatchedText = (
        text: SuggestedAddressInterface['suggestion'],
        matches: SuggestedAddressInterface['matched']
    ) => {
        const formattedText = [];
        let currentIndex = 0;
        matches.sort(([startA], [startB]) => startA - startB);
        matches.forEach(([start, end]) => {
            if (currentIndex < start) {
                formattedText.push(text.slice(currentIndex, start));
            }
            formattedText.push(<strong key={start}>{text.slice(start, end)}</strong>);
            currentIndex = end;
        });
        if (currentIndex < text.length) {
            formattedText.push(text.slice(currentIndex));
        }

        return formattedText.length > 0 ? formattedText : text;
    };

    const handleStreetInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value: inputValue } = e.target;

        setSuggestedAddresses(null);

        if (timeoutIdRef.current) {
            clearTimeout(timeoutIdRef.current);
        }

        if (inputValue) {
            timeoutIdRef.current = setTimeout(() => {
                setLoading(true);
                checkAddressSuggestions(inputValue);
            }, 500);
            setDropdownIsOpen(true);
        } else {
            setDropdownIsOpen(false);
        }
    };

    useEffect(
        () => () => {
            if (timeoutIdRef.current) {
                clearTimeout(timeoutIdRef.current);
            }
        },
        []
    );

    return formLibrary === 'rhf' ? (
        <StreetAddressLineRHF
            dropdownIsOpen={dropdownIsOpen}
            suggestedAddresses={suggestedAddresses}
            handleSuggestedAddressClick={handleSuggestedAddressClick}
            loading={loading}
            setDropdownIsOpen={setDropdownIsOpen}
            formatMatchedText={formatMatchedText}
            handleStreetInput={handleStreetInput}
            containerRef={containerRef}
        />
    ) : (
        <StreetAddressLineFormik
            dropdownIsOpen={dropdownIsOpen}
            suggestedAddresses={suggestedAddresses}
            handleSuggestedAddressClick={handleSuggestedAddressClick}
            loading={loading}
            setDropdownIsOpen={setDropdownIsOpen}
            formatMatchedText={formatMatchedText}
            handleStreetInput={handleStreetInput}
            containerRef={containerRef}
        />
    );
}

type StreetAddressFieldBinding = {
    name: string;
    value: string;
    isError: boolean;
    fieldNote?: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onBlur: React.FocusEventHandler<HTMLInputElement>;
    inputRef?: React.Ref<HTMLInputElement>;
};

type RenderStreetAddressProps = {
    dropdownIsOpen: boolean;
    suggestedAddresses: SuggestedAddressInterface[] | null;
    handleSuggestedAddressClick: (address: SuggestedAddressInterface) => void | Promise<void>;
    loading: boolean;
    setDropdownIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
    formatMatchedText: (
        text: SuggestedAddressInterface['suggestion'],
        matches: SuggestedAddressInterface['matched']
    ) => string | (string | React.JSX.Element)[];
    handleStreetInput: (e: React.ChangeEvent<HTMLInputElement>) => void;
    containerRef: React.RefObject<HTMLDivElement | null>;
};

function StreetAddressFieldView({
    binding,
    dropdownIsOpen,
    suggestedAddresses,
    handleSuggestedAddressClick,
    loading,
    setDropdownIsOpen,
    formatMatchedText,
    handleStreetInput,
    containerRef,
}: Readonly<
    RenderStreetAddressProps & {
        binding: StreetAddressFieldBinding;
    }
>) {
    return (
        <div className={componentClassname} ref={containerRef}>
            <TextField
                type="text"
                data-testid="streetAddressInput"
                data-cs-mask
                label="Street address"
                name={binding.name}
                ref={binding.inputRef}
                isError={binding.isError}
                fieldNote={binding.fieldNote}
                value={binding.value}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    binding.onChange(e);
                    handleStreetInput(e);
                }}
                onFocus={() => {
                    setDropdownIsOpen(true);
                }}
                onBlur={binding.onBlur}
            />
            {dropdownIsOpen && suggestedAddresses && binding.value.length > 0 && (
                <Dropdown className={`${componentClassname}__dropdown`} data-cs-mask>
                    {suggestedAddresses.length > 0
                        ? suggestedAddresses.map(address => (
                              <TextPassage
                                  key={address.format}
                                  className={`${componentClassname}__fpo`}
                                  size="lg"
                                  onClick={() => handleSuggestedAddressClick(address)}
                                  data-testid="address-fpo"
                              >
                                  {formatMatchedText(address.suggestion, address.matched)}
                              </TextPassage>
                          ))
                        : !loading && (
                              <TextPassage key="no-results-found" className={`${componentClassname}__fpo`}>
                                  No results found.
                              </TextPassage>
                          )}
                </Dropdown>
            )}
            {loading && <LoadingIndicator iconSize="lg" className={`${componentClassname}__loader`} />}
        </div>
    );
}

function StreetAddressLineFormik(props: Readonly<RenderStreetAddressProps>) {
    const [{ value, onChange, onBlur }, { touched, error }] = useField('address.streetAddress');

    return (
        <StreetAddressFieldView
            {...props}
            binding={{
                name: 'address.streetAddress',
                value,
                isError: touched && !!error,
                fieldNote: touched ? getFieldErrorMessage(error) : undefined,
                onChange,
                onBlur,
            }}
        />
    );
}

function StreetAddressLineRHF(props: Readonly<RenderStreetAddressProps>) {
    const {
        register,
        watch,
        formState: { errors, touchedFields, isSubmitted },
    } = useFormContext<{ address: AddressFormInterface }>();
    const streetAddressField = register('address.streetAddress');
    const value = watch('address.streetAddress') ?? '';
    const streetAddressError = getFieldErrorMessage(
        (errors.address as { streetAddress?: unknown } | undefined)?.streetAddress
    );
    const showStreetAddressError = !!(touchedFields.address?.streetAddress || isSubmitted) && !!streetAddressError;

    return (
        <StreetAddressFieldView
            {...props}
            binding={{
                name: streetAddressField.name,
                value,
                isError: showStreetAddressError,
                fieldNote: showStreetAddressError ? streetAddressError : undefined,
                onChange: streetAddressField.onChange,
                onBlur: streetAddressField.onBlur,
                inputRef: streetAddressField.ref,
            }}
        />
    );
}
